import {useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {Planning, StudentNews} from '../../../redux/Slice/studentSlice'
import SocialMedia from 'app/modules/student/SocialMedia'
import StdAccountPayment from './StdAccountPayment'

const StudentDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [news, setNews] = useState<any[]>([])
  const [planning, setPlanning] = useState<any>(null)
  const [openSemester, setOpenSemester] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [openNews, setOpenNews] = useState<number[]>([])

  useEffect(() => {
    dispatch(StudentNews()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setNews(res.payload)
        setLoader(false)
      }
    })

    dispatch(Planning()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') setPlanning(res.payload)
      setLoader(false)
    })
  }, [])

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const [datePart, timePart] = dateString.split(' ')
    const [day, month, year] = datePart.split('-')

    const formatted = `${year}-${month}-${day}T${timePart}`

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }

    return new Date(formatted).toLocaleString('en-GB', options)
  }

  function toSortableDate(dateStr: string): string {
    const [day, month, year] = dateStr.split('-')
    return `${year}-${month}-${day}`
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [news, itemsPerPage])

  if (loader) return <Loader />

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = news.slice(indexOfFirstItem, indexOfLastItem)

  const totalPages = Math.ceil(news.length / itemsPerPage)

  return (
    <>
      <div className='container-fluid p-0'>
        <div className='card mb-3 p-0 p-none'>
          <div className='card-header border-0 bg-white py-4 rounded-top-4'>
            <h3 className='fw-bold text-primary m-0'>{t('GLOBAL.DASHBOARD')}</h3>
          </div>
        </div>
      </div>
      <StdAccountPayment />
      {planning && planning.semesters && planning.semesters.length > 0 && (
        <div className='my-3 p-none'>
          {planning.semesters.map((sem: any, index: number) => {
            const collapseId = `semesterCollapse${index}`
            const isOpen = openSemester === index

            return (
              <div key={index} className='card my-3 shadow-sm'>
                <div
                  className='card-header bg-primary text-white d-flex justify-content-between align-items-center'
                  style={{cursor: 'pointer', minHeight: '40px'}}
                  data-bs-toggle='collapse'
                  data-bs-target={`#${collapseId}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenSemester(isOpen ? null : index)}
                >
                  <span className='fw-bold fs-4'>
                    {t('LABEL.ACADEMIC_YEAR')}: <strong>{planning.academic_year}</strong>
                    <span className='mx-3'>
                      {t('LABEL.SEMESTER')}: <strong>{t('LABEL.' + sem.semester)}</strong>
                    </span>
                    <span className='mx-3'>{t('LABEL.COURSE_PLANNING')}</span>
                  </span>
                  <i
                    className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-white fw-bold`}
                    style={{
                      transition: '0.5',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  ></i>
                </div>

                <div className={`collapse ${isOpen ? 'show' : ''}`} id={collapseId}>
                  <div className='card-body'>
                    <div className='table-responsive'>
                      <table className='table table-bordered align-middle'>
                        <thead className='bg-primary'>
                          <tr>
                            <th className='text-white' style={{width: '3%'}}>
                              #
                            </th>
                            <th className='text-white' style={{width: '20%'}}>
                              {t('LABEL.COURSE')}
                            </th>
                            <th className='text-white' style={{width: '15%'}}>
                              {t('LABEL.LECTURER')}
                            </th>
                            <th className='text-white' style={{width: '50%'}}>
                              {t('LABEL.DATES')}
                            </th>
                            <th className='text-white' style={{width: '12%'}}>
                              {t('LABEL.EXAM_DATE_TIME')}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {sem.courses.length > 0 ? (
                            sem.courses.map((course: any, i: number) => (
                              <tr key={course.id}>
                                <th className='text-center bg-primary text-white'>{i + 1}</th>

                                <td>
                                  {course.name}
                                  <br />
                                  <span className='text-muted'>{course.code}</span>
                                </td>

                                <td>{course.lecturer_name}</td>

                                <td>
                                  {course.dates
                                    .slice()
                                    .sort((a: any, b: any) => {
                                      const dateA = new Date(toSortableDate(a.date)).getTime()
                                      const dateB = new Date(toSortableDate(b.date)).getTime()
                                      return dateA - dateB
                                    })
                                    .map((date: any, idx: number) => (
                                      <span
                                        key={idx}
                                        className='badge bg-primary text-white m-1'
                                        style={{whiteSpace: 'pre-line'}}
                                      >
                                        {date.date}
                                        {'\n'}
                                        {date.time_slot}
                                      </span>
                                    ))}
                                </td>

                                <td>{course.exam_date}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className='text-center'>
                                {t('LABEL.NO_COURSES')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* NEWS SECTION */}
      <div className='my-4' id='print-news-section'>
        <div className='card mb-3'>
          <div className='card-header border-0 bg-white py-4 rounded-top-4 d-flex justify-content-between align-items-center'>
            <h3 className='fw-bold text-primary m-0'>{t('LABEL.NEWS_AND_ANNOUNCEMENT')}</h3>

            <div className='d-flex align-items-center gap-3 p-none' style={{minWidth: '350px'}}>
              <button
                className='btn btn-sm btn-outline-primary p-none'
                onClick={() => {
                  if (openNews.length === currentItems.length) {
                    setOpenNews([])
                  } else {
                    setOpenNews(currentItems.map((item: any) => item.id))
                  }
                }}
              >
                <span
                  className={`mx-2 fas ${
                    openNews.length === currentItems.length ? 'fa-chevron-up' : 'fa-chevron-down'
                  }`}
                ></span>
                {openNews.length === currentItems.length
                  ? t('LABEL.COLLAPSE_ALL')
                  : t('LABEL.EXPAND_ALL')}
              </button>

              {/* Print Button */}
              <button
                className='btn btn-sm btn-primary p-none'
                onClick={() => {
                  const printContents = document.getElementById('print-news-section')?.innerHTML
                  const originalContents = document.body.innerHTML

                  if (printContents) {
                    document.body.innerHTML = printContents
                    window.print()
                    document.body.innerHTML = originalContents
                    window.location.reload()
                  }
                }}
              >
                <span className='fas fa-print mx-2'></span>
                {t('BUTTONS.PRINT')}
              </button>

              {/* Records Per Page */}
              <div className='d-flex align-items-center p-none'>
                <label className='mt-2 mx-2 fw-bold text-nowrap p-none'>
                  {t('GLOBAL.RECORD_PER_PAGE')}
                </label>

                <select
                  className='form-select form-select-sm p-none'
                  style={{width: '110px'}}
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {currentItems.map((item: any) => (
          <div key={item.id} className='card mb-3 shadow-sm rounded-4'>
            <div
              className={`card-header d-flex flex-column justify-content-center bg-${
                item.priority === 'Normal' ? 'primary' : 'danger'
              }`}
              style={{minHeight: '40px', cursor: 'pointer'}}
              onClick={() => {
                if (openNews.includes(item.id)) {
                  setOpenNews(openNews.filter((id) => id !== item.id))
                } else {
                  setOpenNews([...openNews, item.id])
                }
              }}
            >
              <h5 className='card-title fw-bold text-white mb-0'>{item.title}</h5>
              <small className='text-light mx-3 fw-bold mb-1'>
                <i className='fas fa-calendar-alt mx-1'></i>
                {formatDate(item.date)}
              </small>
            </div>

            {/* PURE REACT TOGGLE */}
            {openNews.includes(item.id) && (
              <div>
                <div className='card-body'>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: item.content,
                    }}
                  ></p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <SocialMedia />
    </>
  )
}

export default StudentDashboard
